import type { FC } from 'react';
import { useCallback, useEffect } from 'react';

import type { ErrorResponse } from '@remix-run/react';
import { isRouteErrorResponse, useAsyncError, useRouteError } from '@remix-run/react';

import { useObservability } from 'platform/observability';

import {
  ErrorPageContent,
  useTrackGenericErrorPageViewEvent,
} from 'shared/routes/errors/GenericErrorPage';
import { NotFoundPage } from 'shared/routes/errors/NotFoundPage';

import { CustomerBoundaryUI } from './CustomerBoundary';
import {
  SystemError,
  TeamError,
  UnauthenticatedError,
  UnauthorizedError,
  UserError,
} from './Error';

/**
 * This component is responsible for tracking the error in observability and rendering the appropriate error page.
 * NOTE: This component should ONLY be used as a top-level error boundary in the application.
 * @returns The error page to render.
 */
export const RemixErrorHandler = () => {
  const asyncError = useAsyncError();
  const routeError = useRouteError();
  const error = useTrackCaughtError(asyncError ?? routeError);
  return <ErrorUI error={error} />;
};

const ErrorUI: FC<{ error: unknown }> = ({ error }) => {
  if (isRouteErrorResponse(error)) {
    if (error?.status === 404) {
      return <NotFoundPage />;
    }
  }
  if (error instanceof UnauthenticatedError) {
    return <CustomerBoundaryUI authenticationError={error} />;
  }
  if (error instanceof UnauthorizedError) {
    return <CustomerBoundaryUI authorizationError={error} />;
  }
  return <ErrorPageContent />;
};

const useTrackCaughtError = (error: unknown) => {
  const routeError = isRouteErrorResponse(error) ? error : undefined;
  const { trackError } = useObservability();
  const { trackGenericErrorPageView } = useTrackGenericErrorPageViewEvent();

  /**
   * Tracks the error in observability with team data.
   */
  const trackBoundaryError = useCallback(
    (reason: string, error: ErrorResponse | TeamError | Error) => {
      trackGenericErrorPageView(reason, 'system-error', {
        ...error,
        // Fallsback to CPLAT if team is not provided
        team: 'team' in error && error.team ? error.team : 'seller',
      });

      trackError(reason, error instanceof Error ? error : new Error(reason), { ...error });
    },
    [trackError, trackGenericErrorPageView],
  );
  useEffect(() => {
    if (routeError) {
      trackBoundaryError(
        '[RouteError] ' + `${routeError.status} - ${routeError.statusText}`,
        routeError,
      );
    } else if (error instanceof TeamError) {
      trackBoundaryError('[TeamError] ' + error.message, error);
    } else if (error instanceof SystemError) {
      trackBoundaryError('[SystemError] ' + error.message, error);
    } else if (error instanceof UserError) {
      trackBoundaryError('[UserError] ' + error.message, error);
    } else if (error instanceof Error) {
      trackBoundaryError('[Error] ' + error.message, error);
    } else if (error instanceof UnauthenticatedError || error instanceof UnauthorizedError) {
      // Auth errors do not trigger an `error-page-view`
      const errorType =
        error instanceof UnauthenticatedError ? '[UnauthenticatedError] ' : '[UnauthorizedError] ';
      const reason = errorType + error.message;

      trackError(reason, error instanceof Error ? error : new Error(reason), {
        ...error,
      });
    } else if (error instanceof Response) {
      trackBoundaryError(
        '[ResponseThrown] ' + `${error.status} - ${error.statusText ?? 'Failed to redirect'}`,
        new Error('failed-to-redirect'),
      );
    } else {
      trackBoundaryError(
        '[UnknownError]' + String(error),
        new TeamError(new Error(String(error)), 'system-error', 'seller'),
      );
    }
  }, [error, routeError, trackBoundaryError, trackError]);
  return routeError ?? error;
};
